/**
 * Cart drawer "Complete the Look" upsell carousel.
 *
 * The markup lives inside a [data-ajax-cart-section] wrapper, so liquid-ajax-cart
 * replaces it after every cart mutation. All handlers are therefore delegated on
 * document — nothing needs re-binding, and no state survives a re-render.
 *
 * The add button opens the quick-view panel instead of adding directly; that
 * lives in component-cart-quick-view.js.
 */

/* Scroll by exactly one card so the track stays snapped to a card boundary. */
function scrollByCard(track, direction) {
  const card = track.querySelector('.hy-upsell__card');
  if (!card) return;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  /* Honour the OS reduced-motion preference: jump instead of animating. */
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  track.scrollBy({
    left: direction * (card.offsetWidth + gap),
    behavior: reduced ? 'auto' : 'smooth',
  });
}

function syncArrows(track) {
  const root = track.closest('[data-cart-upsell]');
  if (!root) return;
  const maxScroll = track.scrollWidth - track.clientWidth;
  root.toggleAttribute('data-cart-upsell-scrollable', maxScroll > 1);
  for (const arrow of root.querySelectorAll('[data-cart-upsell-scroll]')) {
    const forward = Number(arrow.dataset.cartUpsellScroll) > 0;
    arrow.disabled = forward ? track.scrollLeft >= maxScroll - 1 : track.scrollLeft <= 1;
  }
}

document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-cart-upsell-scroll]');
  if (!arrow) return;
  const track = arrow.closest('[data-cart-upsell]')?.querySelector('[data-cart-upsell-track]');
  if (track) scrollByCard(track, Number(arrow.dataset.cartUpsellScroll));
});

/* `scroll` does not bubble — capture it on document instead. */
document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (track instanceof Element && track.matches('[data-cart-upsell-track]')) syncArrows(track);
  },
  true
);

/* --------------------------------------------------- recommendations fill */

const RECOMMENDATIONS_SECTION = 'cart-upsell-recommendations';
/* The Product Recommendations API caps `limit` at 10. */
const RECOMMENDATIONS_MAX = 10;

async function fetchRecommendationCards(productId, limit) {
  const base = window.Shopify?.routes?.root || '/';
  const url = new URL(`${base}recommendations/products`, window.location.origin);
  url.searchParams.set('section_id', RECOMMENDATIONS_SECTION);
  url.searchParams.set('product_id', productId);
  url.searchParams.set('limit', String(Math.min(limit, RECOMMENDATIONS_MAX)));
  url.searchParams.set('intent', 'related');

  try {
    const response = await fetch(url);
    if (!response.ok) return [];
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(doc.querySelectorAll('[data-cart-upsell-card]'));
  } catch {
    return [];
  }
}

async function fillRecommendations(root) {
  /* request-end and queue-end both fire for a single mutation; the section is
     replaced on re-render, so these flags start clean each time. */
  if (root.dataset.cartUpsellFilling || root.dataset.cartUpsellFilled) return;

  const track = root.querySelector('[data-cart-upsell-track]');
  const pending = (root.dataset.cartUpsellRecommend || '').split(',').filter(Boolean);
  const limit = Number(root.dataset.cartUpsellLimit) || 0;
  if (!track || pending.length === 0 || limit === 0) return;

  root.dataset.cartUpsellFilling = 'true';

  const seen = new Set([
    ...(root.dataset.cartUpsellExclude || '').split(',').filter(Boolean),
    ...Array.from(track.children, (card) => card.dataset.productId).filter(Boolean),
  ]);

  try {
    for (const productId of pending) {
      if (track.children.length >= limit) break;

      for (const card of await fetchRecommendationCards(productId, limit)) {
        if (track.children.length >= limit) break;
        const id = card.dataset.productId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        track.append(card);
      }
      /* The section may have been replaced by a cart mutation mid-fetch. */
      if (!track.isConnected) return;
    }
  } finally {
    delete root.dataset.cartUpsellFilling;
    root.dataset.cartUpsellFilled = 'true';
  }

  /* Reveal the section only once it actually has something to show. */
  if (track.children.length > 0) delete root.dataset.cartUpsellEmpty;

  syncArrows(track);
}

function fillAllRecommendations() {
  for (const root of document.querySelectorAll('[data-cart-upsell]')) fillRecommendations(root);
}

/* Initial and post-re-render arrow state. */
function syncAllArrows() {
  for (const track of document.querySelectorAll('[data-cart-upsell-track]')) syncArrows(track);
}

function syncAll() {
  syncAllArrows();
  fillAllRecommendations();
}

document.addEventListener('liquid-ajax-cart:request-end', syncAll);
document.addEventListener('liquid-ajax-cart:queue-end', syncAll);
window.addEventListener('resize', syncAllArrows);
syncAll();
